// apps/client/src/services/attachment.ts

import {
  Attachment,
  AttachableType,
  NewAttachment,
  UpdateAttachment,
  AttachmentListResponse,
  AttachmentCreateResponse,
  AttachmentUpdateResponse,
} from '@fsp/types';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Fetch all attachments for a specific entity
 */
export const fetchAttachments = async (
  organisationId: string,
  attachableType: AttachableType,
  attachableId: string,
  signal?: AbortSignal
): Promise<Attachment[]> => {
  const token = localStorage.getItem('token');

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/attachments?attachableType=${attachableType}&attachableId=${attachableId}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      signal,
    }
  );

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorResult.message || 'Failed to fetch attachments');
  }

  const result: AttachmentListResponse = await response.json();
  return result.attachments;
};

/**
 * Create a new attachment with file upload
 */
export const createAttachment = async (
  organisationId: string,
  data: {
    attachableType: AttachableType;
    attachableId: string;
    label: string;
    file: File;
    issueDate?: Date;
    expiryDate?: Date;
    notes?: string;
  },
  signal?: AbortSignal
): Promise<Attachment> => {
  const token = localStorage.getItem('token');

  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('attachableType', data.attachableType);
  formData.append('attachableId', data.attachableId);
  formData.append('label', data.label);

  if (data.issueDate) {
    formData.append('issueDate', data.issueDate.toISOString());
  }

  if (data.expiryDate) {
    formData.append('expiryDate', data.expiryDate.toISOString());
  }

  if (data.notes) {
    formData.append('notes', data.notes);
  }

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/attachments`,
    {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type - browser will set it automatically with boundary
      },
      body: formData,
      signal,
    }
  );

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorResult.message || 'Failed to create attachment');
  }

  const result: AttachmentCreateResponse = await response.json();
  return result.attachment;
};

/**
 * Update an attachment's metadata (not the file itself)
 */
export const updateAttachment = async (
  organisationId: string,
  attachmentId: string,
  data: UpdateAttachment,
  signal?: AbortSignal
): Promise<Attachment> => {
  const token = localStorage.getItem('token');

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/attachments/${attachmentId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        ...data,
        issueDate: data.issueDate ? data.issueDate.toISOString() : undefined,
        expiryDate: data.expiryDate ? data.expiryDate.toISOString() : undefined,
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorResult.message || 'Failed to update attachment');
  }

  const result: AttachmentUpdateResponse = await response.json();
  return result.attachment;
};

/**
 * Delete an attachment
 */
export const deleteAttachment = async (
  organisationId: string,
  attachmentId: string,
  signal?: AbortSignal
): Promise<void> => {
  const token = localStorage.getItem('token');

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/attachments/${attachmentId}`,
    {
      method: 'DELETE',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      signal,
    }
  );

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorResult.message || 'Failed to delete attachment');
  }
};

/**
 * Download an attachment document
 */
export const downloadAttachment = (attachment: Attachment): void => {
  // Open the document URL in a new tab to trigger download
  window.open(attachment.documentUrl, '_blank');
};

/**
 * Fetch expiring and expired attachments across the organization
 */
export const fetchExpiringAttachments = async (
  organisationId: string,
  daysAhead: number = 30,
  signal?: AbortSignal
): Promise<import('@fsp/types').AttachmentWithContext[]> => {
  const token = localStorage.getItem('token');

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/attachments/expiring?daysAhead=${daysAhead}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      signal,
    }
  );

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorResult.message || 'Failed to fetch expiring attachments');
  }

  const result: import('@fsp/types').AttachmentExpiringResponse = await response.json();
  return result.attachments;
};
